import React from "react";
import { Check, Lock, RotateCcw } from "lucide-react";

import { cn } from "../../lib/utils";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../ui/sheet";
import usePrefTickets from "../../hooks/usePrefTickets";
import useLayoutTickets from "../../hooks/useLayoutTickets";
import { valorFixoPeloCargo } from "../../services/prefsTickets";
import {
  LAYOUTS_TICKETS,
  gravarLayoutTickets,
} from "../../services/layoutTickets";
import {
  COLUNAS_PAINEL,
  IDS_COLUNAS_PAINEL,
} from "../TicketListItem/colunasPainel";
import {
  ITENS_DO_CARTAO,
  IDS_ITENS_DO_CARTAO,
} from "../TicketListItem/itensDoCartao";
import {
  BOTOES_DA_LINHA,
  IDS_BOTOES_DA_LINHA,
} from "../TicketListItem/botoesDaLinha";
import {
  FERRAMENTAS_DA_LISTA,
  IDS_FERRAMENTAS_DA_LISTA,
} from "./ferramentasDaLista";

// Lista de marcar/desmarcar. São vários grupos com o mesmo desenho (colunas,
// itens da linha, botões); repetir o bloco só renderia divergência entre eles.
// `travados` são os itens que o CARGO fixou: aparecem com cadeado e não
// respondem ao clique. Quem barra de verdade é a leitura da preferência
// (services/prefsTickets.js); aqui é só a explicação de por que não muda.
export const GrupoDeMarcar = ({
  titulo,
  ajuda,
  opcoes,
  marcados,
  aoMudar,
  travados = [],
}) => (
  <div className="mt-1 border-t pt-1">
    <p className="px-2 py-1 text-xs font-medium text-muted-foreground">
      {titulo}
    </p>
    {ajuda && <Ajuda>{ajuda}</Ajuda>}
    {opcoes.map((opcao) => {
      const visivel = marcados.includes(opcao.id);
      const travado = travados.includes(opcao.id);

      return (
        <button
          key={opcao.id}
          type="button"
          aria-pressed={visivel}
          disabled={travado}
          title={
            travado ? "Definido pelo cargo — fale com um administrador." : undefined
          }
          // Atualização a partir do valor anterior, não do render: marcar
          // duas caixas em sequência rápida faria a segunda partir da lista
          // velha e desfazer a primeira.
          onClick={() =>
            !travado &&
            aoMudar((atual) =>
              atual.includes(opcao.id)
                ? atual.filter((id) => id !== opcao.id)
                : [...atual, opcao.id],
            )
          }
          className={cn(
            "flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors",
            travado ? "cursor-not-allowed opacity-60" : "hover:bg-accent",
          )}
        >
          <Check
            className={cn(
              "mt-0.5 h-4 w-4 shrink-0",
              visivel ? "opacity-100" : "opacity-0",
            )}
          />
          {/* A dica fica escrita, não num balão: quem configura nem sempre
              sabe o que é "selo da conexão", e no toque balão não abre. */}
          <span className="min-w-0 flex-1">
            <span className="block truncate">{opcao.rotulo}</span>
            {opcao.dica && (
              <span className="block text-[11px] leading-snug text-muted-foreground">
                {opcao.dica}
              </span>
            )}
          </span>
          {travado && (
            <Lock className="mt-1 h-3 w-3 shrink-0 text-muted-foreground" />
          )}
        </button>
      );
    })}
    {opcoes.some((o) => travados.includes(o.id)) && (
      <div className="px-2">
        <LegendaDaTrava />
      </div>
    )}
  </div>
);

const Ajuda = ({ children }) => (
  <p className="px-2 pb-1 text-[11px] leading-snug text-muted-foreground">
    {children}
  </p>
);

const LegendaDaTrava = () => (
  <p className="pt-1 text-[11px] text-muted-foreground">
    Com cadeado: definido pelo cargo.
  </p>
);

// Escolha única em botões lado a lado. `travado` = o cargo fixou o valor.
const Escolha = ({ titulo, opcoes, valor, aoMudar, travado = false }) => (
  <fieldset
    className="mt-1 border-t px-2 pb-1 pt-2"
    title={
      travado ? "Definido pelo cargo — fale com um administrador." : undefined
    }
  >
    <legend className="sr-only">{titulo}</legend>
    <p
      aria-hidden
      className="flex items-center gap-1.5 pb-1.5 text-xs font-medium text-muted-foreground"
    >
      {titulo}
      {travado && <Lock className="h-3 w-3 shrink-0" />}
    </p>
    <div className="flex flex-wrap gap-1">
      {opcoes.map(([id, rotulo, dica]) => (
        <button
          key={String(id)}
          type="button"
          title={dica}
          aria-pressed={valor === id}
          disabled={travado}
          onClick={() => aoMudar(id)}
          className={cn(
            "rounded-md border px-2.5 py-3.5 text-xs transition-colors sm:py-1",
            valor === id
              ? "border-primary/40 bg-primary/10 text-foreground"
              : "text-muted-foreground hover:bg-accent",
            travado && "cursor-not-allowed opacity-60",
          )}
        >
          {rotulo}
        </button>
      ))}
    </div>
    {/* O que a opção escolhida faz, escrito: o `title` das outras não
        aparece no toque. */}
    {opcoes.find(([id]) => id === valor)?.[2] && (
      <p className="pt-1.5 text-[11px] leading-snug text-muted-foreground">
        {opcoes.find(([id]) => id === valor)[2]}
      </p>
    )}
    {/* O `title` não aparece no toque: a explicação fica escrita. */}
    {travado && <LegendaDaTrava />}
  </fieldset>
);

// Os pontos de partida da maquete (opções A-D). Não são layouts fixos: cada
// um é só um conjunto de preferências, e a pessoa ajusta peça por peça depois.
// "Ações em massa" da maquete são resolverTodos + transferirTodos; filtros e
// o próprio botão de personalizar não se escondem. Os avisos (SLA, venda)
// ficam em todos: escondê-los não é questão de gosto.
const AVISOS = ["slaVencendo", "vendaAguardando"];
const naBarraSo = (...ids) =>
  IDS_FERRAMENTAS_DA_LISTA.filter((id) => !ids.includes(id));

const PRESETS = [
  {
    id: "hoje",
    nome: "Hoje",
    dica: "Como a tela sempre foi, com tudo à vista.",
    prefs: {
      navegacaoLista: "abas",
      resolvidosComoAba: false,
      campoBuscaMensagem: "campo",
      escopoBuscaMensagem: "aba",
      densidadeLinha: "confortavel",
      itensDoCartao: IDS_ITENS_DO_CARTAO,
      colunasPainel: IDS_COLUNAS_PAINEL,
      botoesDaLinha: IDS_BOTOES_DA_LINHA,
      ferramentasDaLista: IDS_FERRAMENTAS_DA_LISTA,
      ferramentasNoMenu: [],
    },
  },
  {
    id: "a",
    nome: "A · Enxuta",
    dica: "Só o essencial: foto, prévia e tempo de espera.",
    prefs: {
      navegacaoLista: "abas",
      resolvidosComoAba: true,
      campoBuscaMensagem: "icone",
      densidadeLinha: "confortavel",
      itensDoCartao: ["avatar", "previa", "espera", ...AVISOS],
      ferramentasDaLista: IDS_FERRAMENTAS_DA_LISTA,
      ferramentasNoMenu: naBarraSo("novo"),
    },
  },
  {
    id: "b",
    nome: "B · Caixa de entrada",
    dica: "Coluna lateral com pastas, como uma caixa de e-mail.",
    prefs: {
      navegacaoLista: "lateral",
      resolvidosComoAba: true,
      campoBuscaMensagem: "campo",
      densidadeLinha: "confortavel",
      itensDoCartao: [
        "avatar",
        "previa",
        "espera",
        "seloFila",
        "faixaDaFila",
        "etiquetas",
        ...AVISOS,
      ],
      // Favoritas e Pausa viram pastas do trilho lateral.
      ferramentasDaLista: IDS_FERRAMENTAS_DA_LISTA,
      ferramentasNoMenu: naBarraSo("novo", "ordem", "favoritas", "adiados"),
    },
  },
  {
    id: "c",
    nome: "C · Supervisor",
    dica: "Tabela com colunas, para acompanhar a equipe.",
    tabela: true,
    prefs: {
      navegacaoLista: "abas",
      resolvidosComoAba: true,
      campoBuscaMensagem: "campo",
      itensDoCartao: IDS_ITENS_DO_CARTAO,
      colunasPainel: IDS_COLUNAS_PAINEL,
      ferramentasDaLista: IDS_FERRAMENTAS_DA_LISTA,
      ferramentasNoMenu: naBarraSo(
        "novo",
        "ordem",
        "resolverTodos",
        "transferirTodos",
      ),
    },
  },
  {
    id: "d",
    nome: "D · Seletor único",
    dica: "Uma caixa de escolha e linhas baixas: cabe mais na tela.",
    prefs: {
      navegacaoLista: "seletor",
      resolvidosComoAba: true,
      campoBuscaMensagem: "campo",
      densidadeLinha: "compacta",
      itensDoCartao: ["previa", "espera", ...AVISOS],
      ferramentasDaLista: IDS_FERRAMENTAS_DA_LISTA,
      ferramentasNoMenu: naBarraSo("novo"),
    },
  },
];

const nomeDoLayout = (id) => LAYOUTS_TICKETS.find((l) => l.id === id)?.nome;

// O painel "Personalizar lista". Fica por cima, sem escurecer nada: a lista
// de verdade atrás dele é a prévia — cada clique aplica na hora.
// `children` é o botão que abre (vai como gatilho).
const PersonalizarLista = ({ children }) => {
  const layout = useLayoutTickets();
  const [ladoDaLista] = usePrefTickets("ladoDaLista", "esquerda");

  // Todas as chaves que um preset pode mexer, num mapa: aplicar um preset é
  // percorrer o preset e chamar o setter de cada uma.
  const p = {
    navegacaoLista: usePrefTickets("navegacaoLista", "abas"),
    resolvidosComoAba: usePrefTickets("resolvidosComoAba", false),
    campoBuscaMensagem: usePrefTickets("campoBuscaMensagem", "campo"),
    escopoBuscaMensagem: usePrefTickets("escopoBuscaMensagem", "aba"),
    densidadeLinha: usePrefTickets("densidadeLinha", "confortavel"),
    itensDoCartao: usePrefTickets("itensDoCartao", IDS_ITENS_DO_CARTAO),
    colunasPainel: usePrefTickets("colunasPainel", IDS_COLUNAS_PAINEL),
    botoesDaLinha: usePrefTickets("botoesDaLinha", IDS_BOTOES_DA_LINHA),
    ferramentasDaLista: usePrefTickets(
      "ferramentasDaLista",
      IDS_FERRAMENTAS_DA_LISTA,
    ),
    ferramentasNoMenu: usePrefTickets("ferramentasNoMenu", []),
  };
  const valor = (chave) => p[chave][0];
  const mudar = (chave, v) => p[chave][1](v);

  const aplicar = (preset) => {
    Object.entries(preset.prefs).forEach(([chave, v]) => {
      // Valor único que o cargo fixou não muda com preset.
      if (valorFixoPeloCargo(chave)) return;
      const [, set] = p[chave];
      // A posição (barra/menu) de ferramenta travada também fica.
      const travados =
        p[chave === "ferramentasNoMenu" ? "ferramentasDaLista" : chave][2];
      // O que o cargo travou fica como está: o preset só mexe no resto.
      set(
        Array.isArray(v) && travados.length
          ? (atual) => [
              ...v.filter((id) => !travados.includes(id)),
              ...atual.filter((id) => travados.includes(id)),
            ]
          : v,
      );
    });
    if (preset.tabela) gravarLayoutTickets("painel");
    else if (layout === "painel") gravarLayoutTickets("classico");
  };

  // Qual ponto de partida bate com a tela de agora. Ajustou à mão: nenhum.
  const igual = (a, b) =>
    Array.isArray(b)
      ? Array.isArray(a) && a.length === b.length && b.every((x) => a.includes(x))
      : a === b;
  const presetAtual = PRESETS.find(
    (preset) =>
      Boolean(preset.tabela) === (layout === "painel") &&
      Object.entries(preset.prefs).every(([chave, v]) => igual(valor(chave), v)),
  );

  // "Tabela" não é densidade: é o layout Painel.
  const linha = layout === "painel" ? "tabela" : valor("densidadeLinha");
  const mudarLinha = (v) => {
    if (v === "tabela") return gravarLayoutTickets("painel");
    mudar("densidadeLinha", v);
    if (layout === "painel") gravarLayoutTickets("classico");
  };

  // Três estados por ferramenta, nas duas listas que já existem: fora de
  // `ferramentasDaLista` = oculto; dentro e em `ferramentasNoMenu` = menu ⋯.
  const [ferramentas, setFerramentas, ferramentasTravadas] =
    p.ferramentasDaLista;
  const [noMenu, setNoMenu] = p.ferramentasNoMenu;
  const ondeFica = (id) =>
    !ferramentas.includes(id) ? "oculto" : noMenu.includes(id) ? "menu" : "barra";
  const mudarOnde = (id, onde) => {
    setFerramentas((a) =>
      onde === "oculto"
        ? a.filter((x) => x !== id)
        : a.includes(id)
          ? a
          : [...a, id],
    );
    setNoMenu((a) =>
      onde === "menu"
        ? a.includes(id)
          ? a
          : [...a, id]
        : a.filter((x) => x !== id),
    );
  };

  return (
    <Sheet modal={false}>
      <SheetTrigger asChild>{children}</SheetTrigger>
      <SheetContent
        // Do lado oposto ao da lista, para não cobrir a prévia.
        side={ladoDaLista === "direita" ? "left" : "right"}
        // Mexer na lista atrás (rolar, abrir um atendimento) não fecha o
        // painel: ver o efeito é o ponto. Fecha no X ou no Esc.
        onInteractOutside={(e) => e.preventDefault()}
        className="flex w-full max-w-none flex-col gap-0 overflow-y-auto p-0 sm:max-w-sm"
      >
        <SheetHeader className="border-b p-4 pr-10 text-left">
          <SheetTitle className="text-base">Personalizar lista</SheetTitle>
          <SheetDescription className="text-xs">
            Muda na hora a lista de atendimentos. Vale só para você.
          </SheetDescription>
        </SheetHeader>

        <div className="p-2">
          <div className="px-2 pb-2 pt-1">
            <p className="pb-1.5 text-xs font-medium text-muted-foreground">
              Começar de
            </p>
            <div className="flex flex-wrap gap-1">
              {PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  title={preset.dica}
                  aria-pressed={presetAtual === preset}
                  onClick={() => aplicar(preset)}
                  className={cn(
                    "rounded-md border px-2.5 py-3.5 text-xs transition-colors sm:py-1",
                    presetAtual === preset
                      ? "border-primary/40 bg-primary/10 text-foreground"
                      : "hover:bg-accent",
                  )}
                >
                  {preset.nome}
                </button>
              ))}
            </div>
            <p className="pt-1.5 text-[11px] leading-snug text-muted-foreground">
              {presetAtual
                ? presetAtual.dica
                : "Escolha um ponto de partida e ajuste o resto abaixo, peça por peça."}
            </p>
          </div>

          <Escolha
            titulo="Trocar de recorte"
            opcoes={[
              ["abas", "Abas no topo", "Minhas, Nossas e Aguardando lado a lado, em cima da lista."],
              ["lateral", "Coluna lateral", "As abas viram uma coluna ao lado da lista, como pastas."],
              ["seletor", "Seletor", "Uma caixa de escolha só, para sobrar espaço para a lista."],
            ]}
            valor={valor("navegacaoLista")}
            aoMudar={(v) => mudar("navegacaoLista", v)}
            travado={valorFixoPeloCargo("navegacaoLista")}
          />
          <Escolha
            titulo="Resolvidos"
            opcoes={[
              [true, "Aba própria", "Os resolvidos viram mais uma aba, junto das outras."],
              [false, "Botão Abertos/Fechados", "Um botão troca entre os abertos e os já resolvidos."],
            ]}
            valor={valor("resolvidosComoAba")}
            aoMudar={(v) => mudar("resolvidosComoAba", v)}
          />
          <Escolha
            titulo="Busca nas mensagens"
            opcoes={[
              ["campo", "Campo à vista", "O campo de buscar nas mensagens fica sempre aparecendo."],
              ["icone", "Atrás de ícone", "O campo fica escondido atrás de uma lupa até você precisar."],
            ]}
            valor={valor("campoBuscaMensagem")}
            aoMudar={(v) => mudar("campoBuscaMensagem", v)}
          />
          <Escolha
            titulo="A busca nas mensagens procura"
            opcoes={[
              ["aba", "Só na aba aberta", "Procura só nos atendimentos da aba em que você está."],
              ["todas", "Em todas", "Procura em todos os atendimentos que você pode ver."],
            ]}
            valor={valor("escopoBuscaMensagem")}
            aoMudar={(v) => mudar("escopoBuscaMensagem", v)}
            travado={valorFixoPeloCargo("escopoBuscaMensagem")}
          />
          <Escolha
            titulo="Linha"
            opcoes={[
              ["confortavel", "Confortável", "Mais espaço e mais detalhes em cada atendimento."],
              ["compacta", "Compacta", "Linhas mais baixas: cabem mais atendimentos na tela."],
              ["tabela", "Tabela", "Uma grade com colunas, boa para acompanhar muitos de uma vez."],
            ]}
            valor={linha}
            aoMudar={mudarLinha}
          />
          {layout !== "painel" && (
            <Escolha
              titulo="Ao abrir um atendimento"
              opcoes={[
                [
                  "classico",
                  `${nomeDoLayout("classico")}: lista ao lado`,
                  "A lista continua aparecendo ao lado da conversa aberta.",
                ],
                [
                  "foco",
                  `${nomeDoLayout("foco")}: só a conversa`,
                  "A conversa ocupa a tela toda; a lista volta ao fechar.",
                ],
              ]}
              valor={layout}
              aoMudar={gravarLayoutTickets}
            />
          )}

          {/* Colunas só existem na Tabela; no cartão não há coluna. */}
          {layout === "painel" ? (
            <GrupoDeMarcar
              titulo="Colunas"
              ajuda="O que aparece em cada coluna da tabela."
              opcoes={COLUNAS_PAINEL}
              marcados={valor("colunasPainel")}
              aoMudar={(v) => mudar("colunasPainel", v)}
              travados={p.colunasPainel[2]}
            />
          ) : (
            <GrupoDeMarcar
              titulo="Na linha aparece"
              ajuda="O que cada atendimento mostra na lista. Marcado aparece."
              opcoes={ITENS_DO_CARTAO}
              marcados={valor("itensDoCartao")}
              aoMudar={(v) => mudar("itensDoCartao", v)}
              travados={p.itensDoCartao[2]}
            />
          )}

          <div className="mt-1 border-t pt-1">
            <p className="px-2 py-1 text-xs font-medium text-muted-foreground">
              Ferramentas da lista
            </p>
            <Ajuda>
              Botões acima da lista. Barra: fica à vista. Menu: dentro do ⋯.
              Oculto: some da tela.
            </Ajuda>
            {FERRAMENTAS_DA_LISTA.map((f) => {
              const travado = ferramentasTravadas.includes(f.id);
              const onde = ondeFica(f.id);

              return (
                <div
                  key={f.id}
                  role="group"
                  aria-label={f.rotulo}
                  title={
                    travado
                      ? "Definido pelo cargo — fale com um administrador."
                      : undefined
                  }
                  className="flex items-center gap-2 px-2 py-1"
                >
                  <span className="min-w-0 flex-1 text-sm">
                    <span className="block truncate">{f.rotulo}</span>
                    <span className="block text-[11px] leading-snug text-muted-foreground">
                      {f.dica}
                    </span>
                  </span>
                  {travado && (
                    <Lock className="h-3 w-3 shrink-0 text-muted-foreground" />
                  )}
                  <div className="flex shrink-0 overflow-hidden rounded-md border">
                    {[
                      // No trilho lateral Favoritas e Pausa moram na coluna.
                      [
                        "barra",
                        valor("navegacaoLista") === "lateral" &&
                        (f.id === "favoritas" || f.id === "adiados")
                          ? "Coluna"
                          : "Barra",
                      ],
                      ["menu", "Menu"],
                      ["oculto", "Oculto"],
                    ].map(([id, rotulo]) => (
                      <button
                        key={id}
                        type="button"
                        aria-pressed={onde === id}
                        disabled={travado}
                        onClick={() => mudarOnde(f.id, id)}
                        className={cn(
                          "px-2 py-3.5 text-xs transition-colors sm:py-0.5",
                          onde === id
                            ? "bg-primary/10 text-foreground"
                            : "text-muted-foreground hover:bg-accent",
                          travado && "cursor-not-allowed opacity-60",
                        )}
                      >
                        {rotulo}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
            {ferramentasTravadas.length > 0 && (
              <div className="px-2">
                <LegendaDaTrava />
              </div>
            )}
          </div>

          <GrupoDeMarcar
            titulo="Botões da linha"
            ajuda="Atalhos que surgem ao passar o mouse num atendimento. Tudo continua dentro da conversa."
            opcoes={BOTOES_DA_LINHA}
            marcados={valor("botoesDaLinha")}
            aoMudar={(v) => mudar("botoesDaLinha", v)}
            travados={p.botoesDaLinha[2]}
          />

          <div className="mt-1 border-t px-2 pt-3">
            {/* Não é o "Restaurar padrão" de Configurações: aquele também
                apaga visões salvas e filtros. Aqui volta só a aparência. */}
            <button
              type="button"
              onClick={() => aplicar(PRESETS[0])}
              className="flex items-center gap-1.5 rounded-md px-2 py-3.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground sm:py-1"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Voltar ao padrão
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default PersonalizarLista;
